/**
 * Server detail page. Mirrors ApplicationDetail's shape: header with edit/delete
 * affordances, an About card listing metadata, hostname/description inline-editable.
 * Components-on-this-server lives on the components themselves (component → server
 * is a downstream relationship that lands when access methods do), so for v1 we
 * keep the page focused on the server's own identity.
 */
import {
  HardDrive,
  Loader2,
  Building2,
  Pencil,
  Trash2,
  ChevronLeft,
  Save,
  X,
  Globe,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError } from '@/api/client.js';

export function ServerDetailPage(): JSX.Element {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const search = useSearch({ strict: false }) as { org?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The list-all endpoint already includes org info, mirroring the pattern used
  // on the applications page — saves a second round-trip and works as a coherence
  // check that the user can still see this server.
  const listQuery = useQuery({ queryKey: ['servers'], queryFn: () => api.servers.listAll() });
  const server = (listQuery.data ?? []).find(
    (s) => s.slug === slug && (search.org ? s.orgSlug === search.org : true),
  );

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    if (!server) return;
    setName(server.name);
    setHostname(server.hostname ?? '');
    setDescription(server.description ?? '');
    setEditing(true);
    setError(null);
  }

  async function save() {
    if (!server) return;
    setSaving(true);
    setError(null);
    try {
      await api.servers.update(server.id, {
        name,
        hostname: hostname.length > 0 ? hostname : null,
        description: description.length > 0 ? description : null,
      });
      toast.success('Server updated');
      await queryClient.invalidateQueries({ queryKey: ['servers'] });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function destroy() {
    if (!server) return;
    if (!confirm(`Delete ${server.name}? This cannot be undone.`)) return;
    try {
      await api.servers.delete(server.id);
      toast.success(`Deleted ${server.name}`);
      await queryClient.invalidateQueries({ queryKey: ['servers'] });
      await navigate({ to: '/p/applications/servers' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete server.');
    }
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!server) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm">
          <HardDrive className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p>Server not found, or you don't have access to it.</p>
          <Button variant="link" onClick={() => navigate({ to: '/p/applications/servers' })}>
            Back to servers
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/p/applications/servers"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" />
          All servers
        </Link>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <HardDrive className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="text-xl font-semibold" />
            ) : (
              <h1 className="text-2xl font-semibold tracking-tight">{server.name}</h1>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Link
                to="/orgs/$slug"
                params={{ slug: server.orgSlug }}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <Building2 className="h-3.5 w-3.5" />
                {server.orgName}
              </Link>
              <span>·</span>
              <span className="font-mono">/{server.slug}</span>
              {server.hostname && !editing && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" />
                  <span className="font-mono">{server.hostname}</span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={save} disabled={saving || !name}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={startEdit}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="ghost" onClick={destroy}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </header>

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
          <CardDescription>Top-level metadata for this server.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="hostname">Hostname</Label>
                <Input
                  id="hostname"
                  value={hostname}
                  onChange={(e) => setHostname(e.target.value)}
                  placeholder="web-01.acme.example"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="desc">Description</Label>
                <Input
                  id="desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="(optional)"
                />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {server.description ?? <span className="italic">No description.</span>}
            </p>
          )}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Row label="Hostname" value={server.hostname ?? 'not set'} mono={!!server.hostname} />
            <Row label="Server id" value={server.id} mono />
            <Row label="Created" value={new Date(server.createdAt).toLocaleString()} />
            <Row label="Updated" value={new Date(server.updatedAt).toLocaleString()} />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Access methods</CardTitle>
          <CardDescription>
            How DeployIt reaches this server (SSH / docker / kubectl).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="rounded-md border border-dashed border-border/70 bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
            Access methods land in a future phase. For now the server is just an identity
            you can attach components to.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{value}</dd>
    </>
  );
}
